import { useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { CanceledError } from "axios";
import { useErrorBoundary } from "react-error-boundary";
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from "@shared/lib/utils";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { AccountSearch, AccountWithRelations } from "@models/account";
import { buildAccountSort, searchCustomersWithRelations, SearchParams } from "../../../api/accounts.service";
import { useEntityBrowse } from "../../../hooks/useEntityBrowse";
import { CUSTOMER_ACCOUNT_TYPE } from "./customers.constants";
import {
  DEFAULT_SORT_KEY,
  parseCustomersUrlParams,
  syncCustomersUrlParams,
} from "./customersUrlParams";

/**
 * Manages customers browse state: search form, URL sync, sorting, data fetch, and reset.
 */
export function useCustomersBrowse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBoundary } = useErrorBoundary();

  const urlParams = useMemo(() => parseCustomersUrlParams(searchParams), [searchParams]);

  const [data, setData] = useState<AccountWithRelations[]>([]);
  const [sortKey, setSortKey] = useState(urlParams.sortKey || DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(urlParams.sortMethod);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const accountSearchForm = useForm<AccountSearch>({
    defaultValues: {
      account_type: CUSTOMER_ACCOUNT_TYPE,
      ...(urlParams.filters ?? {}),
    },
  });



  /**
   * Fetches customers with relations using current sort and the given search filters.
   * @param searchData filter values from the search form
   */
  async function getData(searchData: AccountSearch): Promise<void> {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    try {
      const sort = buildAccountSort(sortKey, sortMethod);
      const payload: SearchParams = {
        filter: {
          account_type: CUSTOMER_ACCOUNT_TYPE,
          ...deleteEmptyFieldsFromObject(searchData),
        },
        sort: [sort],
      };
      const accounts = await searchCustomersWithRelations(
        payload,
        abortControllerRef.current.signal,
      );
      setData(accounts.data);
      setIsLoading(false);
    } catch (error: unknown) {
      if (error instanceof CanceledError) {
        return;
      }
      handleErrorWithBoundary(error, showBoundary);
      setIsLoading(false);
    }
  }



  /**
   * Handles search submit: syncs URL and refetches table data.
   * @param formData current search form values
   */
  const onSearch: SubmitHandler<AccountSearch> = (formData) => {
    syncCustomersUrlParams(setSearchParams, formData, sortKey, sortMethod);
    getData(formData);
  };

  useEntityBrowse({
    form: accountSearchForm,
    sortKey,
    sortMethod,
    onSearch,
  });



  /**
   * Updates the field used for table sorting when changed in DataTable.
   * @param value sort field key
   */
  function handleSortKeyChanged(value: string) {
    setSortKey(value);
  }



  /**
   * Updates sort direction when changed in DataTable.
   * @param value "ASC" or any other string (treated as DESC)
   */
  function handleSortMethodChanged(value: string) {
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }



  /**
   * Clears all search fields and restores the default customer account type.
   * Triggers a new search via useEntityBrowse form watch.
   */
  function reset() {
    const values = setAllValuesToNull(accountSearchForm.getValues());

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        accountSearchForm.setValue(key as keyof AccountSearch, values[key as keyof AccountSearch]);
      }
    }

    accountSearchForm.setValue("account_type", CUSTOMER_ACCOUNT_TYPE);
  }

  return {
    accountSearchForm,
    data,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    reset,
  };
}
